import { createFileRoute, Link, notFound, useRouter } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ExternalLink, Truck, ArrowLeft } from "lucide-react";
import { BackButton } from "@/components/BackButton";
import { supplierCategoryLabel } from "@/lib/supplier-categories";
import { getBusinessPublicHead, type BusinessPublicHead } from "@/lib/business-public.functions";
import { useT } from "@/lib/i18n";
import { useSupplierT, supplierText } from "@/lib/suppliers-i18n";
import { ReviewsSection } from "@/components/ReviewsSection";

const ALLOWED_DOMAINS = [
  { key: "link_directclub_digital", label: "directclub.digital" },
  { key: "link_direct_club", label: "direct.club" },
  { key: "link_karmacleaner_digital", label: "karmacleaner.digital" },
] as const;

type PublicSupplier = {
  id: string;
  business_id: string;
  name: string;
  category: string | null;
  categories: string[] | null;
  note: string | null;
  link_directclub_digital: string | null;
  link_direct_club: string | null;
  link_karmacleaner_digital: string | null;
};

const supplierQuery = (businessId: string, supplierId: string) =>
  queryOptions({
    queryKey: ["public-supplier", businessId, supplierId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("business_suppliers")
        .select(
          "id, business_id, name, category, categories, note, link_directclub_digital, link_direct_club, link_karmacleaner_digital",
        )
        .eq("id", supplierId)
        .eq("business_id", businessId)
        .maybeSingle();
      if (error) throw error;
      if (!data) throw notFound();
      return data as PublicSupplier;
    },
  });

const businessQuery = (id: string) =>
  queryOptions({
    queryKey: ["business-public-head", id],
    queryFn: () => getBusinessPublicHead({ data: { id } }),
  });

export const Route = createFileRoute("/podnik/$id/dodavatel/$supplierId")({
  loader: ({ params, context }) => {
    context.queryClient.ensureQueryData(businessQuery(params.id));
    context.queryClient.ensureQueryData(supplierQuery(params.id, params.supplierId));
  },
  head: ({ params }) => {
    const url = `https://cerveza.international/podnik/${params.id}/dodavatel/${params.supplierId}`;
    // SSR/prerender: use EN as the safe default; client re-renders will not
    // affect <head>, but the language tag on <html> keeps parity via i18n.
    const title = supplierText("en", "det.head.title");
    const desc = supplierText("en", "det.head.desc");
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: SupplierDetailPage,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    const t = useSupplierT();
    return (
      <div className="mx-auto max-w-2xl p-8 text-center">
        <h1 className="mb-2 font-serif text-2xl font-bold">{t("det.err.title")}</h1>
        <p className="mb-4 text-muted-foreground">{error.message}</p>
        <Button onClick={() => { reset(); router.invalidate(); }}>{t("det.err.retry")}</Button>
      </div>
    );
  },
  notFoundComponent: () => {
    const { id } = Route.useParams();
    const t = useSupplierT();
    return (
      <div className="mx-auto max-w-2xl p-8 text-center">
        <h1 className="mb-2 font-serif text-2xl font-bold">{t("det.nf.title")}</h1>
        <Link
          to="/podnik/$id"
          params={{ id }}
          className="text-brand-terracotta underline"
        >
          {t("det.nf.back")}
        </Link>
      </div>
    );
  },
});

function SupplierDetailPage() {
  const { id, supplierId } = Route.useParams();
  const { lang } = useT();
  const t = useSupplierT();
  const { data: supplier } = useSuspenseQuery(supplierQuery(id, supplierId));
  const { data: business } = useSuspenseQuery(businessQuery(id));

  const cats = (supplier.categories && supplier.categories.length > 0)
    ? supplier.categories
    : (supplier.category ? [supplier.category] : []);

  const links = ALLOWED_DOMAINS
    .map((d) => ({ ...d, url: supplier[d.key] }))
    .filter((l) => !!l.url);

  const businessName = (business as BusinessPublicHead | null)?.name ?? t("det.businessFallback");

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <BackButton />

      <div className="mt-4">
        <Link
          to="/podnik/$id"
          params={{ id }}
          className="inline-flex items-center gap-1 text-sm text-brand-terracotta hover:underline"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {businessName}
        </Link>
      </div>

      <Card className="mt-3 border-brand-earth/10">
        <div className="p-6">
          <div className="flex items-start gap-3">
            <div className="rounded-md bg-brand-terracotta/10 p-2">
              <Truck className="h-6 w-6 text-brand-terracotta" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="font-serif text-2xl font-bold leading-tight">{supplier.name}</h1>
              <p className="mt-0.5 text-sm text-muted-foreground">
                {t("det.forBusiness", { name: businessName })}
              </p>
            </div>
          </div>

          {cats.length > 0 && (
            <section className="mt-6">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t("det.cats.title")}
              </h2>
              <ul className="mt-2 flex flex-wrap gap-1.5">
                {cats.map((c) => (
                  <li key={c}>
                    <Badge variant="secondary" className="text-xs">
                      {supplierCategoryLabel(c, lang)}
                    </Badge>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {supplier.note && (
            <section className="mt-6">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t("det.note.title")}
              </h2>
              <p className="mt-2 whitespace-pre-line text-sm">{supplier.note}</p>
            </section>
          )}

          <section className="mt-6">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t("det.ext.title")}
            </h2>
            {links.length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">
                {t("det.ext.empty")}
              </p>
            ) : (
              <ul className="mt-2 grid gap-2 sm:grid-cols-2">
                {links.map((l) => (
                  <li key={l.key}>
                    <a
                      href={l.url!}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="flex items-center justify-between gap-2 rounded-md border border-brand-earth/20 bg-muted/30 px-3 py-2 text-sm text-primary hover:bg-muted"
                    >
                      <span className="inline-flex items-center gap-2">
                        <ExternalLink className="h-4 w-4" />
                        <span className="font-mono">{l.label}</span>
                      </span>
                      <span className="text-xs text-muted-foreground">{t("det.ext.open")}</span>
                    </a>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-3 text-xs text-muted-foreground">
              {t("det.allowed", { list: "directclub.digital, direct.club, karmacleaner.digital" })}
            </p>
          </section>

          <section className="mt-8">
            <ReviewsSection targetType="supplier" targetId={supplier.id} urlKey="rev" />
          </section>
        </div>
      </Card>
    </div>
  );
}
